import React, { useState, useEffect, useMemo } from 'react';
import { databaseService } from '../../services/databaseService';
import jsPDF from 'jspdf';
import styles from './Dashboard.module.css';
import { REGION_BOUNDARIES, parseWKT, pointInPolygon } from '../../data/regionBoundaries';

// Map.js'deki districtFilters - İSTASYON İSMİ'nden bölge çıkarmak için
const DISTRICT_FILTERS = {
  "Akyurt": ["akyurt"],
  "Beypazarı": ["beypazari", "beypazarı", "beypazari"],
  "Çankaya": ["ankaya", "çankaya", "?ankaya", "caldag", "caldağ", "tbmm", "?alda?", "?aldag", "?ankaya/?alda?", "?ankaya/tbmm"],
  "Elmadağ": ["elmadağ", "elmadag", "elmada? barutsan fabrikasi", "elmada? radar sahasi", "elmada?/ye?ildere"],
  "Etimesgut": ["etimesgut", "etimesgut havalimanı", "etimesgut havalimani", "et?mesgut haval?mani", "etimesgut"],
  "Gölbaşı": ["golbasi", "gölbaşı", "g?lba?i", "genclik parki", "gençlik parkı", "ufuk danisman", "ufuk danışman", "altındağ", "g?lba?i/ah?boz k?y?", "g?lba?i/ufuk dani?ment", "g?lba?i/ya?lipinar k?y?"],
  "Haymana": ["haymana", "haymana tarim"],
  "Keçiören": ["kecoren", "keçiören", "mgm", "ke???ren/9. (ankara) b?lge", "ke???ren/ba?lum", "ke?i?ren/mgm"],
  "Kazan": ["kazan", "kahramankazan", "kazan/kurtbo?azi baraji", "kazan/sarayk?y toprak"],
  "Mamak": ["mamak", "bayindir baraji", "bayındır barajı", "mamak/bayindir baraji"],
  "Nallıhan": ["nallihan", "nallıhan", "nallihan/sariyar"],
  "Polatlı": ["polatli", "polatlı"],
  "Pursaklar": ["pursaklar"],
  "Sincan": ["sincan", "temelli", "s?ncan", "s?ncan/temell?"], // ? karakteri ile encode edilmiş versiyonlar
  "Yenimahalle": ["yenimahalle"]
};

// İSTASYON İSMİ'nden bölge çıkar (Map.js'deki mantık)
const extractRegionFromIstasyonIsmi = (istasyonIsmi) => {
  if (!istasyonIsmi || typeof istasyonIsmi !== 'string') return null;
  
  // Normalize Turkish characters (including ? character)
  const normalized = istasyonIsmi.toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/Ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/Ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/Ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/Ç/g, 'c')
    .replace(/\?/g, ''); // ? karakterlerini kaldır (encoding sorunu)
  
  // Her bölge için filtreleri kontrol et
  for (const [region, filters] of Object.entries(DISTRICT_FILTERS)) {
    for (const filter of filters) {
      const normalizedFilter = filter.toLowerCase()
        .replace(/ı/g, 'i')
        .replace(/İ/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/Ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/Ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/Ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/Ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/Ç/g, 'c')
        .replace(/\?/g, ''); // ? karakterlerini kaldır
      
      // Multiple matching strategies:
      // 1. Direct string comparison (case-insensitive)
      // 2. Normalized comparison (without Turkish chars)
      // 3. Check if filter is substring of istasyon or vice versa
      if (normalized.includes(normalizedFilter) || 
          normalizedFilter.includes(normalized) ||
          normalized === normalizedFilter) {
        return region;
      }
    }
  }
  
  return null;
};

const Reports = () => {
  const [allParks, setAllParks] = useState([]); // Tüm parklar (bölge bilgisi ile)
  const [availableRegions, setAvailableRegions] = useState([]); // Veritabanından çekilen bölgeler
  const [subRegions, setSubRegions] = useState([]); // Alt bölgeler
  const [selectedRegions, setSelectedRegions] = useState([]); // Seçilen bölgeler (çoklu)
  const [selectedSubRegions, setSelectedSubRegions] = useState([]); // Seçilen alt bölgeler
  const [searchTerm, setSearchTerm] = useState(''); // Arama terimi
  const [selectedPark, setSelectedPark] = useState(null);
  const [parkData, setParkData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigEnabled, setEmailConfigEnabled] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Park listesini yükle
  useEffect(() => {
    analyzeDatabase();
    loadParks();
  }, []);

  // Veritabanını analiz et ve bölge bilgisini bul
  const analyzeDatabase = async () => {
    try {
      console.log('Veritabani analizi baslatiliyor...');
      
      // Önce tablo yapısını kontrol et
      const structure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
      const columnNames = structure.map(col => col.column_name);
      console.log('Tum kolonlar:', columnNames);
      
      // Örnek veri çek (10 satır)
      const sampleData = await databaseService.getTableData('parks', 'stage_z_z_park_ismi_txt', 10, 0);
      console.log('Örnek veri (10 satır):', sampleData);
      
      // Her kolonun benzersiz değerlerini kontrol et
      if (sampleData.rows && sampleData.rows.length > 0) {
        const firstRow = sampleData.rows[0];
        console.log('İlk satırın tüm key\'leri:', Object.keys(firstRow));
        console.log('İlk satır verisi:', firstRow);
        
        // Her kolon için benzersiz değerleri göster
        for (const column of columnNames) {
          const uniqueValues = [...new Set(
            sampleData.rows
              .map(row => {
                // Tüm olası key formatlarını dene
                return row[column] || 
                       row[column.toLowerCase()] || 
                       row[column.toUpperCase()] ||
                       row[`"${column}"`] ||
                       null;
              })
              .filter(Boolean)
          )].slice(0, 5); // İlk 5 benzersiz değer
          
          if (uniqueValues.length > 0) {
            console.log(`"${column}" kolonu örnek değerleri:`, uniqueValues);
          }
        }
      }
      
      // Tüm parkları çek ve bölge bilgisini analiz et
      const allParksData = await databaseService.getTableData('parks', 'stage_z_z_park_ismi_txt', 100, 0);
      if (allParksData.rows && allParksData.rows.length > 0) {
        // Her kolon için benzersiz değerleri bul
        for (const column of columnNames) {
          const allValues = allParksData.rows
            .map(row => {
              return row[column] || 
                     row[column.toLowerCase()] || 
                     row[column.toUpperCase()] ||
                     row[`"${column}"`] ||
                     null;
            })
            .filter(Boolean);
          
          const uniqueValues = [...new Set(allValues)];
          
          // Eğer bu kolon bölge gibi görünüyorsa (az sayıda benzersiz değer varsa)
          if (uniqueValues.length > 0 && uniqueValues.length < 50) {
            console.log(`"${column}" kolonu muhtemelen bölge bilgisi (${uniqueValues.length} benzersiz değer):`, uniqueValues.slice(0, 20));
          }
        }
      }
    } catch (err) {
      console.error('Veritabanı analizi hatası:', err);
    }
  };

  // Bölge seçimine göre alt bölgeleri yükle
  useEffect(() => {
    if (selectedRegions.length > 0) {
      loadSubRegions(selectedRegions);
    } else {
      setSubRegions([]);
      setSelectedSubRegions([]);
    }
  }, [selectedRegions]);

  // Her bölge için park sayısını hesapla
  const regionParkCounts = useMemo(() => {
    const counts = {};
    allParks.forEach(park => {
      const region = park.region || park.originalRegion || '';
      if (region) {
        counts[region] = (counts[region] || 0) + 1;
      }
    });
    return counts;
  }, [allParks]);

  // Filtrelenmiş parklar (bölge, alt bölge ve arama terimine göre)
  const filteredParks = useMemo(() => {
    let filtered = allParks;

    // Bölge filtresi - veritabanındaki gerçek isimlerle eşleştir (tam eşleşme)
    if (selectedRegions.length > 0) {
      filtered = filtered.filter(park => {
        const parkRegion = park.region || park.originalRegion || '';
        if (!parkRegion) return false;
        
        // Tam eşleşme kontrolü (case-insensitive)
        return selectedRegions.some(selectedRegion => {
          const parkRegionTrimmed = parkRegion.trim();
          const selectedRegionTrimmed = selectedRegion.trim();
          
          // Tam eşleşme (öncelikli)
          if (parkRegionTrimmed === selectedRegionTrimmed ||
              parkRegionTrimmed.toLowerCase() === selectedRegionTrimmed.toLowerCase()) {
            return true;
          }
          
          // Normalize edilmiş karşılaştırma
          const parkNormalized = parkRegionTrimmed
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          const selectedNormalized = selectedRegionTrimmed
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          
          return parkNormalized === selectedNormalized;
        });
      });
    }

    // Alt bölge filtresi
    if (selectedSubRegions.length > 0) {
      filtered = filtered.filter(park => {
        const parkSubRegion = park.subRegion || park.neighborhood || park.mahalle || '';
        return selectedSubRegions.some(subRegion => 
          parkSubRegion.toLowerCase().includes(subRegion.toLowerCase()) ||
          parkSubRegion.toLowerCase() === subRegion.toLowerCase()
        );
      });
    }

    // Arama filtresi
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(park => 
        park.name.toLowerCase().includes(searchLower) ||
        (park.region && park.region.toLowerCase().includes(searchLower)) ||
        (park.subRegion && park.subRegion.toLowerCase().includes(searchLower)) ||
        String(park.id).includes(searchTerm)
      );
    }

    console.log('Filtreleme sonucu:', {
      selectedRegions,
      selectedSubRegions,
      searchTerm,
      totalParks: allParks.length,
      filteredParks: filtered.length,
      sampleFiltered: filtered.slice(0, 5).map(p => ({ name: p.name, region: p.region, id: p.id })),
      filteredRegions: [...new Set(filtered.map(p => p.region).filter(Boolean))]
    });
    
    // Eğer bölge seçildiyse ama filtrelenmiş park yoksa, debug bilgisi göster
    if (selectedRegions.length > 0 && filtered.length === 0) {
      console.warn('Bölge seçildi ama park bulunamadı!');
      console.warn('   Seçilen bölgeler:', selectedRegions);
      console.warn('   Mevcut park bölgeleri:', [...new Set(allParks.map(p => p.region).filter(Boolean))]);
      console.warn('   Örnek parklar:', allParks.slice(0, 10).map(p => ({ name: p.name, region: p.region })));
    }

    return filtered;
  }, [allParks, selectedRegions, selectedSubRegions, searchTerm]);

  const loadParks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let parksList = [];
      const allRegionsSet = new Set(Object.keys(DISTRICT_FILTERS)); // Tüm bölgeleri başlangıçta ekle
      
      // Koordinat bazlı bölge tespit fonksiyonu
      const detectRegionFromCoordinates = (coords) => {
        if (!coords || !Array.isArray(coords) || coords.length < 2) {
          return null;
        }
        
        const [lng, lat] = coords;
        
        // Geçerli koordinat kontrolü (Ankara için yaklaşık koordinat aralığı: lng: 32.5-33.0, lat: 39.8-40.1)
        if (isNaN(lng) || isNaN(lat)) {
          return null;
        }
        
        // Ankara dışındaki koordinatları filtrele
        if (lng < 32.0 || lng > 33.5 || lat < 39.5 || lat > 40.5) {
          console.warn(`Ankara dışı koordinat: [${lng}, ${lat}]`);
          return null;
        }
        
        // Her bölge için point-in-polygon kontrolü
        for (const [region, boundary] of Object.entries(REGION_BOUNDARIES)) {
          if (!boundary || !Array.isArray(boundary) || boundary.length < 3) {
            continue; // Boş boundary'leri atla
          }
          
          // Boundary'nin formatını kontrol et
          let polygon = boundary;
          
          // Eğer boundary iç içe array ise ([[lng, lat], [lng, lat], ...])
          if (Array.isArray(boundary[0]) && Array.isArray(boundary[0][0])) {
            // En dıştaki polygon'u al
            polygon = boundary[0];
          } else if (Array.isArray(boundary[0]) && typeof boundary[0][0] === 'number') {
            // Zaten doğru formatta [[lng, lat], [lng, lat], ...]
            polygon = boundary;
          }
          
          // Polygon'un geçerli olduğunu kontrol et
          if (!polygon || polygon.length < 3) {
            continue;
          }
          
          // Point-in-polygon kontrolü
          try {
            if (pointInPolygon([lng, lat], polygon)) {
              return region;
            }
          } catch (err) {
            console.warn(`Point-in-polygon hatası (${region}):`, err);
            continue;
          }
        }
        
        return null;
      };
      
      // En yakın bölgeyi bul (fallback - eğer point-in-polygon çalışmazsa)
      const findNearestRegion = (coords) => {
        if (!coords || !Array.isArray(coords) || coords.length < 2) {
          return null;
        }
        
        const [lng, lat] = coords;
        
        // Geçerli koordinat kontrolü
        if (isNaN(lng) || isNaN(lat)) {
          return null;
        }
        
        // Ankara dışındaki koordinatları filtrele
        if (lng < 32.0 || lng > 33.5 || lat < 39.5 || lat > 40.5) {
          return null;
        }
        
        // Eğer REGION_BOUNDARIES boşsa veya yetersizse, koordinat aralıklarına göre tahmin et
        // Bu geçici bir çözüm - gerçek boundary'ler eklendiğinde kaldırılabilir
        const regionBounds = {
          'Çankaya': { minLng: 32.75, maxLng: 33.0, minLat: 39.85, maxLat: 40.05 },
          'Etimesgut': { minLng: 32.55, maxLng: 32.75, minLat: 39.85, maxLat: 40.05 },
          'Keçiören': { minLng: 32.75, maxLng: 33.0, minLat: 40.0, maxLat: 40.15 },
          'Mamak': { minLng: 32.85, maxLng: 33.0, minLat: 39.75, maxLat: 39.9 },
          'Sincan': { minLng: 32.45, maxLng: 32.65, minLat: 39.85, maxLat: 40.05 },
          'Yenimahalle': { minLng: 32.65, maxLng: 32.85, minLat: 39.85, maxLat: 40.05 },
          'Akyurt': { minLng: 33.0, maxLng: 33.2, minLat: 40.0, maxLat: 40.2 },
          'Beypazarı': { minLng: 31.5, maxLng: 32.0, minLat: 40.0, maxLat: 40.3 },
          'Elmadağ': { minLng: 33.0, maxLng: 33.3, minLat: 39.9, maxLat: 40.1 },
          'Gölbaşı': { minLng: 32.8, maxLng: 33.0, minLat: 39.7, maxLat: 39.85 },
          'Haymana': { minLng: 32.0, maxLng: 32.5, minLat: 39.4, maxLat: 39.7 },
          'Kazan': { minLng: 32.6, maxLng: 32.8, minLat: 40.0, maxLat: 40.2 },
          'Nallıhan': { minLng: 31.5, maxLng: 32.0, minLat: 40.1, maxLat: 40.4 },
          'Polatlı': { minLng: 32.0, maxLng: 32.5, minLat: 39.5, maxLat: 39.85 },
          'Pursaklar': { minLng: 32.7, maxLng: 32.9, minLat: 40.0, maxLat: 40.15 }
        };
        
        // Koordinat aralıklarına göre bölge bul
        for (const [region, bounds] of Object.entries(regionBounds)) {
          if (lng >= bounds.minLng && lng <= bounds.maxLng && 
              lat >= bounds.minLat && lat <= bounds.maxLat) {
            return region;
          }
        }
        
        return null;
      };
      
      // 2. Veritabanından parkları yükle - koordinat bazlı bölge tespiti ile
      try {
        console.log('Veritabanından parklar yükleniyor (koordinat bazlı bölge tespiti ile)...');
        
        // Önce tüm şemaları ve tabloları kontrol et
        const schemas = await databaseService.listSchemas();
        console.log('Mevcut şemalar:', schemas);
        
        // Her şemadaki tabloları kontrol et
        for (const schema of schemas) {
          try {
            const tables = await databaseService.listTables(schema);
            console.log(`${schema} şemasındaki tablolar:`, tables.slice(0, 10)); // İlk 10 tablo
            
            // Park ismi içeren tabloları bul
            const parkTables = tables.filter(table => 
              table.toLowerCase().includes('park') ||
              table.toLowerCase().includes('ismi') ||
              table.toLowerCase().includes('name')
            );
            
            if (parkTables.length > 0) {
              console.log(`${schema} şemasında park tabloları bulundu:`, parkTables);
            }
          } catch (err) {
            console.warn(`${schema} şeması okunamadı:`, err);
          }
        }
        const structure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
        const columnNames = structure.map(col => col.column_name);
        
        console.log('Tüm tablo kolonları:', columnNames);
        
        // Park adı kolonunu bul
        const nameColumn = columnNames.find(col => 
          col.toLowerCase().includes('park') && 
          (col.toLowerCase().includes('ismi') || col.toLowerCase().includes('name'))
        ) || columnNames.find(col => col.toLowerCase() === 'name') || columnNames[1] || columnNames[0];
        
        // ID kolonunu bul
        const idColumn = columnNames.find(col => col.toLowerCase() === 'id') || columnNames[0];
        
        // İSTASYON İSMİ kolonunu bul (Map.js'deki gibi)
        let foundRegionColumn = columnNames.find(col => {
          const colName = col;
          return colName === 'İSTASYON İSMİ' ||
                 colName === 'İSTASYON_İSMİ' ||
                 colName.toLowerCase() === 'istasyon ismi' ||
                 colName.toLowerCase() === 'istasyon_ismi' ||
                 colName.includes('İSTASYON') ||
                 colName.includes('istasyon');
        });
        
        // Eğer bulunamazsa diğer olasılıkları dene
        if (!foundRegionColumn) {
          foundRegionColumn = columnNames.find(col => {
            const colName = col.toLowerCase();
            return colName.includes('region') ||
                   colName.includes('district') ||
                   colName.includes('ilçe') ||
                   colName.includes('bölge') ||
                   colName.includes('station') ||
                   colName === 'district_name' ||
                   colName === 'region_name' ||
                   colName === 'city' ||
                   colName === 'area';
          });
        }
        
        // Eğer hala bulunamazsa, büyük harfli kolonları kontrol et
        if (!foundRegionColumn) {
          foundRegionColumn = columnNames.find(col => {
            const colName = col;
            return colName.includes('BÖLGE') ||
                   colName.includes('İLÇE') ||
                   colName === 'BÖLGE' ||
                   colName === 'İLÇE';
          });
        }
        
        console.log('Bulunan bölge kolonu:', foundRegionColumn || 'BULUNAMADI');
        console.log('Park adı kolonu:', nameColumn);
        console.log('ID kolonu:', idColumn);
        
        // Alt bölge/mahalle kolonunu bul
        const subRegionColumn = columnNames.find(col => {
          const colName = col.toLowerCase();
          return colName.includes('mahalle') ||
                 colName.includes('neighborhood') ||
                 colName.includes('subregion') ||
                 colName.includes('altbölge');
        });
        
        // Önce örnek bir sorgu çalıştır ve kolon adlarını kontrol et
        const testQuery = `SELECT * FROM parks.stage_z_z_park_ismi_txt LIMIT 1`;
        console.log('Test sorgusu:', testQuery);
        const testResult = await databaseService.executeQuery(testQuery);
        
        if (testResult.rows && testResult.rows.length > 0) {
          console.log('İlk satırın tüm kolonları:', Object.keys(testResult.rows[0]));
          console.log('İlk satır verisi:', testResult.rows[0]);
        }
        
        // WKT kolonunu bul (koordinat bilgisi için)
        const wktColumn = columnNames.find(col => 
          col.toLowerCase() === 'wkt' || 
          col.toLowerCase().includes('geometry') ||
          col.toLowerCase().includes('coord')
        );
        
        console.log('WKT kolonu:', wktColumn || 'BULUNAMADI');
        
        // Tüm parkları çek - kolon adlarını doğru şekilde kullan
        let selectColumns = [];
        
        // ID ve name kolonlarını ekle
        selectColumns.push(idColumn);
        selectColumns.push(nameColumn);
        
        // WKT kolonunu ekle (koordinat bazlı bölge tespiti için)
        if (wktColumn) {
          selectColumns.push(wktColumn);
        }
        
        // Bölge kolonunu ekle - PostgreSQL'de büyük harfli kolonlar için tırnak gerekli
        if (foundRegionColumn) {
          // Kolon adında boşluk veya özel karakter varsa tırnak içine al
          if (foundRegionColumn.includes(' ') || foundRegionColumn !== foundRegionColumn.toLowerCase()) {
            selectColumns.push(`"${foundRegionColumn}"`);
          } else {
            selectColumns.push(foundRegionColumn);
          }
        }
        
        // Alt bölge kolonunu ekle
        if (subRegionColumn) {
          if (subRegionColumn.includes(' ') || subRegionColumn !== subRegionColumn.toLowerCase()) {
            selectColumns.push(`"${subRegionColumn}"`);
          } else {
            selectColumns.push(subRegionColumn);
          }
        }
        
        console.log('Çekilecek kolonlar:', selectColumns);
        
        const query = `SELECT DISTINCT ${selectColumns.join(', ')} FROM parks.stage_z_z_park_ismi_txt WHERE ${nameColumn} IS NOT NULL ORDER BY ${nameColumn} LIMIT 5000`;
        console.log('Çalıştırılan sorgu:', query);
        const result = await databaseService.executeQuery(query);
        
        console.log(`Veritabanından ${result.rows?.length || 0} park çekildi`);
        
        // İlk birkaç satırı göster
        if (result.rows && result.rows.length > 0) {
          console.log('İlk 3 park örneği (ham veri):', result.rows.slice(0, 3));
        }
        
        if (result.rows && result.rows.length > 0) {
          // Park isimlerinden bölge çıkar - daha katı anahtar kelimeler (sadece tam eşleşmeler)
          const regionKeywords = {
            'Çankaya': ['çankaya', 'cankaya', 'kızılay', 'kizilay', 'bahçelievler', 'bahcelievler', 'kavaklıdere', 'kavaklidere'],
            'Etimesgut': ['etimesgut', 'etimesgut'],
            'Keçiören': ['keçiören', 'kecioren'],
            'Mamak': ['mamak'],
            'Sincan': ['sincan', 'temelli'],
            'Yenimahalle': ['yenimahalle', 'yeni mahalle'],
            'Akyurt': ['akyurt'],
            'Beypazarı': ['beypazarı', 'beypazari'],
            'Elmadağ': ['elmadağ', 'elmadag'],
            'Gölbaşı': ['gölbaşı', 'golbasi'],
            'Haymana': ['haymana'],
            'Kazan': ['kazan'],
            'Nallıhan': ['nallıhan', 'nallihan'],
            'Polatlı': ['polatlı', 'polatli'],
            'Pursaklar': ['pursaklar']
          };
          
          parksList = result.rows.map((row, index) => {
            // Bölge bilgisini direkt veritabanından al - tüm olası key formatlarını dene
            let parkRegion = '';
            if (foundRegionColumn) {
              // Tüm olası key formatlarını dene
              const possibleKeys = [
                foundRegionColumn,
                foundRegionColumn.toLowerCase(),
                foundRegionColumn.toUpperCase(),
                `"${foundRegionColumn}"`,
                `"${foundRegionColumn.toLowerCase()}"`,
                `"${foundRegionColumn.toUpperCase()}"`
              ];
              
              // Önce direkt key'leri kontrol et
              for (const key of possibleKeys) {
                if (row.hasOwnProperty(key) && row[key] !== null && row[key] !== undefined) {
                  parkRegion = String(row[key]).trim();
                  break;
                }
              }
              
              // Eğer hala bulunamadıysa, tüm key'leri kontrol et (case-insensitive)
              if (!parkRegion) {
                const allKeys = Object.keys(row);
                const matchingKey = allKeys.find(key => {
                  const keyLower = key.toLowerCase().replace(/"/g, '');
                  const colLower = foundRegionColumn.toLowerCase();
                  return keyLower === colLower || 
                         keyLower.includes(colLower) || 
                         colLower.includes(keyLower);
                });
                
                if (matchingKey) {
                  parkRegion = String(row[matchingKey] || '').trim();
                }
              }
            }
            
            let parkSubRegion = '';
            if (subRegionColumn) {
              const possibleKeys = [
                subRegionColumn,
                subRegionColumn.toLowerCase(),
                subRegionColumn.toUpperCase(),
                `"${subRegionColumn}"`
              ];
              
              for (const key of possibleKeys) {
                if (row.hasOwnProperty(key) && row[key] !== null && row[key] !== undefined) {
                  parkSubRegion = String(row[key]).trim();
                  break;
                }
              }
            }
            
            // Debug: İlk birkaç park için bölge bilgisini göster
            if (index < 5) {
              console.log(`Park ${index + 1}:`, {
                name: row[nameColumn] || row[nameColumn.toLowerCase()] || row[Object.keys(row).find(k => k.toLowerCase().includes('name') || k.toLowerCase().includes('ismi'))],
                regionColumn: foundRegionColumn,
                regionValue: parkRegion,
                allKeys: Object.keys(row),
                rowData: row
              });
            }
            
            // ID ve name değerlerini de tüm formatları deneyerek bul
            const parkId = row[idColumn] || 
                          row[idColumn.toLowerCase()] || 
                          row[Object.keys(row).find(k => k.toLowerCase() === 'id')] ||
                          index + 1;
            
            const parkName = row[nameColumn] || 
                            row[nameColumn.toLowerCase()] || 
                            row[Object.keys(row).find(k => (k.toLowerCase().includes('name') || k.toLowerCase().includes('ismi')) && k.toLowerCase().includes('park'))] ||
                            `Park ${index + 1}`;
            
            // Koordinatları her zaman çıkar (bölge tespiti için)
            let coords = null;
            let wktColumn = null;
            let lngColumn = null;
            let latColumn = null;
            
            // WKT kolonundan koordinat çıkar
            wktColumn = columnNames.find(col => 
              col.toLowerCase() === 'wkt' || 
              col.toLowerCase().includes('geometry') ||
              col.toLowerCase().includes('coord')
            );
            
            if (wktColumn) {
              const wktValue = row[wktColumn] || 
                              row[wktColumn.toLowerCase()] || 
                              row[wktColumn.toUpperCase()] ||
                              '';
              
              if (wktValue) {
                coords = parseWKT(String(wktValue));
              }
            }
            
            // Eğer WKT'den koordinat bulunamadıysa, diğer kolonlardan dene
            if (!coords) {
              // longitude ve latitude kolonlarını ara
              lngColumn = columnNames.find(col => 
                col.toLowerCase().includes('lng') || 
                col.toLowerCase().includes('lon') ||
                col.toLowerCase().includes('longitude') ||
                col.toLowerCase() === 'x'
              );
              
              latColumn = columnNames.find(col => 
                col.toLowerCase().includes('lat') ||
                col.toLowerCase().includes('latitude') ||
                col.toLowerCase() === 'y'
              );
              
              if (lngColumn && latColumn) {
                const lng = parseFloat(row[lngColumn] || row[lngColumn.toLowerCase()] || row[lngColumn.toUpperCase()] || 0);
                const lat = parseFloat(row[latColumn] || row[latColumn.toLowerCase()] || row[latColumn.toUpperCase()] || 0);
                
                if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
                  coords = [lng, lat];
                }
              }
            }
            
            // Bölge tespiti: Önce veritabanından gelen bölge bilgisini kullan
            let extractedRegion = parkRegion;
            
            // Eğer veritabanında bölge bilgisi yoksa, İSTASYON İSMİ'nden çıkar (Map.js mantığı)
            if (!extractedRegion && foundRegionColumn) {
              const istasyonIsmi = row[foundRegionColumn] || 
                                  row[foundRegionColumn.toLowerCase()] || 
                                  row[foundRegionColumn.toUpperCase()] ||
                                  row[`"${foundRegionColumn}"`] ||
                                  '';
              
              if (istasyonIsmi) {
                extractedRegion = extractRegionFromIstasyonIsmi(String(istasyonIsmi));
                if (extractedRegion && index < 10) {
                  console.log(`İSTASYON İSMİ'nden bölge tespiti: "${parkName}" → ${extractedRegion} (İSTASYON: ${istasyonIsmi})`);
                }
              }
            }
            
            // Eğer hala bölge bulunamadıysa, koordinat bazlı tespit yap
            if (!extractedRegion && coords && coords.length === 2) {
              // Önce point-in-polygon ile dene
              extractedRegion = detectRegionFromCoordinates(coords);
              
              // Eğer bulunamadıysa, en yakın bölgeyi bul (fallback)
              if (!extractedRegion) {
                extractedRegion = findNearestRegion(coords);
              }
              
              if (extractedRegion) {
                // Debug: Koordinat bazlı tespit için log
                if (index < 100) {
                  console.log(`Koordinat bazlı bölge tespiti: "${parkName}" → ${extractedRegion}`, coords);
                }
              } else {
                // Koordinat var ama bölge bulunamadı
                if (index < 30) {
                  console.log(`Koordinat var ama bölge bulunamadı: "${parkName}"`, coords);
                }
              }
            } else if (!coords && index < 10) {
              // Koordinat bulunamadı
              console.log(`Koordinat bulunamadı: "${parkName}"`);
              console.log(`   WKT kolonu:`, wktColumn);
              console.log(`   Lng/Lat kolonları:`, lngColumn, latColumn);
            }
            
            return {
              id: parkId,
              name: parkName,
              originalId: parkId,
              originalName: parkName,
              region: extractedRegion || '', // Veritabanından, İSTASYON İSMİ'nden veya koordinat bazlı tespit ile çıkarılan bölge
              subRegion: parkSubRegion,
              originalRegion: parkRegion || extractedRegion || '', // Orijinal bölge bilgisi
              source: 'database', // Tüm parklar veritabanından geliyor
              coordinates: coords && coords.length === 2 ? coords : null // Koordinatları sakla (debug için)
            };
          });
          
          // Veritabanından gelen parklardan bölgeleri çıkar
          parksList.forEach(park => {
            if (park.region) {
              allRegionsSet.add(park.region);
            }
          });
          
          // Tüm bölgeleri birleştir ve sırala
          const uniqueRegions = Array.from(allRegionsSet)
            .filter(Boolean)
            .map(r => String(r).trim())
            .sort((a, b) => a.localeCompare(b, 'tr'));
          
          // Her bölge için park sayısını göster
          const regionCounts = {};
          parksList.forEach(park => {
            if (park.region) {
              regionCounts[park.region] = (regionCounts[park.region] || 0) + 1;
            }
          });
          
          console.log('Veritabanından çekilen bölgeler:', uniqueRegions);
          console.log('Bölge başına park sayıları:', regionCounts);
          console.log('Bölge örnekleri (ilk 10):', parksList.slice(0, 10).map(p => ({ name: p.name, region: p.region })));
          console.log('Toplam park sayısı:', parksList.length);
          console.log('Bölge bilgisi olan park sayısı:', parksList.filter(p => p.region).length);
          
          // Her bölge için detaylı istatistik
          const regionStats = {};
          parksList.forEach(park => {
            const region = park.region || 'Bölge Yok';
            if (!regionStats[region]) {
              regionStats[region] = { count: 0, parks: [] };
            }
            regionStats[region].count++;
            if (regionStats[region].parks.length < 5) {
              regionStats[region].parks.push(park.name);
            }
          });
          
          console.log('Bölge istatistikleri (detaylı):', regionStats);
          
          // Özellikle Sincan için kontrol et
          if (regionStats['Sincan']) {
            console.log(`Sincan bölgesi: ${regionStats['Sincan'].count} park bulundu`);
            console.log(`Sincan park örnekleri:`, regionStats['Sincan'].parks);
          } else {
            console.warn('Sincan bölgesinde park bulunamadı!');
            
            // Veritabanındaki park isimlerinde "sincan" veya "temelli" geçenleri bul
            const sincanRelatedParks = parksList.filter(p => {
              const nameLower = (p.name || '').toLowerCase();
              return nameLower.includes('sincan') || nameLower.includes('temelli');
            });
            console.log(`Veritabanında Sincan/Temelli içeren parklar (${sincanRelatedParks.length} adet):`, sincanRelatedParks.slice(0, 10).map(p => ({ name: p.name, region: p.region })));
          }
          
          // Koordinat bazlı tespit istatistikleri
          const coordinateBasedDetections = parksList.filter(p => p.region && !p.originalRegion);
          const istasyonBasedDetections = parksList.filter(p => {
            // İSTASYON İSMİ'nden tespit edilenler (veritabanından bölge bilgisi yok ama İSTASYON İSMİ'nden var)
            return p.region && p.originalRegion && p.region !== p.originalRegion;
          });
          const parksWithoutRegion = parksList.filter(p => !p.region || p.region === '');
          
          console.log(`Koordinat bazlı tespit ile ${coordinateBasedDetections.length} parka bölge atandı`);
          console.log(`İSTASYON İSMİ'nden tespit ile ${istasyonBasedDetections.length} parka bölge atandı`);
          console.log(`Bölge bilgisi olmayan park sayısı: ${parksWithoutRegion.length}`);
          
          if (parksWithoutRegion.length > 0) {
            console.log(`Bölge bilgisi olmayan park örnekleri (ilk 10):`, parksWithoutRegion.slice(0, 10).map(p => ({
              name: p.name,
              hasWKT: !!p.wkt,
              hasCoords: !!p.coordinates
            })));
          }
          
          // Çok fazla park içeren bölgeleri kontrol et (muhtemelen yanlış eşleşme)
          Object.entries(regionStats).forEach(([region, stats]) => {
            if (stats.count > 100) {
              console.warn(`${region} bölgesinde ${stats.count} park var - muhtemelen yanlış eşleşme!`);
              console.warn(`   Örnek parklar:`, stats.parks);
            }
          });
          
          // Duplicate parkları temizle (aynı ID veya normalize edilmiş isim)
          const uniqueParksMap = new Map();
          parksList.forEach(park => {
            const key = `${park.id}_${park.name.toLowerCase().trim()}`;
            if (!uniqueParksMap.has(key)) {
              uniqueParksMap.set(key, park);
            } else {
              // Duplicate bulundu - bölge bilgisi olanı tercih et
              const existing = uniqueParksMap.get(key);
              if (!existing.region && park.region) {
                uniqueParksMap.set(key, park);
              }
            }
          });
          
          parksList = Array.from(uniqueParksMap.values());
          
          console.log(`Duplicate temizleme sonrası: ${parksList.length} benzersiz park`);
          
          setAvailableRegions(uniqueRegions);
        }
      } catch (err) {
        console.warn('parks.stage_z_z_park_ismi_txt bulunamadı:', err);
        
        // z_z_park_ismi tablosunu dene
        try {
          const structure = await databaseService.getTableStructure('parks', 'z_z_park_ismi');
          const columnNames = structure.map(col => col.column_name);
          const nameColumn = columnNames.find(col => col.toLowerCase().includes('park')) || columnNames[1] || columnNames[0];
          const idColumn = columnNames.find(col => col.toLowerCase() === 'id') || columnNames[0];
          
          const query = `SELECT DISTINCT ${idColumn}, ${nameColumn} FROM parks.z_z_park_ismi WHERE ${nameColumn} IS NOT NULL ORDER BY ${nameColumn} LIMIT 5000`;
          const result = await databaseService.executeQuery(query);
          
          if (result.rows && result.rows.length > 0) {
            parksList = result.rows.map((row, index) => ({
              id: row[idColumn] || index + 1,
              name: row[nameColumn] || `Park ${index + 1}`,
              originalId: row[idColumn],
              originalName: row[nameColumn],
              region: '',
              subRegion: '',
              originalRegion: ''
            }));
            
            setAvailableRegions([]);
          }
        } catch (err2) {
          console.warn('parks.z_z_park_ismi de bulunamadı:', err2);
        }
      }
      
      // Final duplicate temizleme - ID ve isim kombinasyonuna göre
      const finalUniqueParks = [];
      const seenParks = new Set();
      
      parksList.forEach(park => {
        // Park ID ve normalize edilmiş isim kombinasyonu
        const normalizedName = (park.name || '')
          .toLowerCase()
          .trim()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9\s]/g, '');
        
        const parkKey = `${park.id}_${normalizedName}`;
        
        if (!seenParks.has(parkKey)) {
          seenParks.add(parkKey);
          finalUniqueParks.push(park);
        } else {
          // Duplicate bulundu - bölge bilgisi olanı tercih et
          const existingIndex = finalUniqueParks.findIndex(p => 
            `${p.id}_${(p.name || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, '')}` === parkKey
          );
          
          if (existingIndex >= 0) {
            const existing = finalUniqueParks[existingIndex];
            if (!existing.region && park.region) {
              finalUniqueParks[existingIndex] = park; // Bölge bilgisi olanı kullan
            }
          }
        }
      });
      
      console.log(`Final duplicate temizleme: ${parksList.length} → ${finalUniqueParks.length} park`);
      
      // Bölge istatistiklerini tekrar hesapla
      const finalRegionStats = {};
      finalUniqueParks.forEach(park => {
        const region = park.region || 'Bölge Yok';
        finalRegionStats[region] = (finalRegionStats[region] || 0) + 1;
      });
      
      console.log('Final bölge istatistikleri:', finalRegionStats);
      
      setAllParks(finalUniqueParks);
      
      if (finalUniqueParks.length === 0) {
        setError('Park bulunamadı. Veritabanında park tablolarını kontrol edin.');
      }
    } catch (err) {
      console.error('Park listesi yükleme hatası:', err);
      setError(err.message || 'Park listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubRegions = async (selectedRegionsList) => {
    try {
      // Seçilen bölgelerdeki parkların alt bölgelerini bul
      const parksInRegions = allParks.filter(park => 
        selectedRegionsList.some(region => 
          park.region && park.region.toLowerCase() === region.toLowerCase()
        )
      );
      
      const uniqueSubRegions = [...new Set(
        parksInRegions
          .map(p => p.subRegion)
          .filter(Boolean)
      )].sort();
      
      setSubRegions(uniqueSubRegions);
    } catch (err) {
      console.error('Alt bölge yükleme hatası:', err);
    }
  };

  const handleRegionToggle = (region) => {
    setSelectedRegions(prev => {
      const newRegions = prev.includes(region)
        ? prev.filter(r => r !== region)
        : [...prev, region];
      
      // Bölge değiştiğinde, seçili park filtrelenmiş listede yoksa sıfırla
      if (selectedPark) {
        const currentFiltered = allParks.filter(park => {
          if (newRegions.length > 0) {
            const parkRegion = park.region || park.originalRegion || '';
            if (!parkRegion) return false;
            return newRegions.some(selectedRegion => {
              const parkRegionNormalized = parkRegion.trim();
              const selectedRegionNormalized = selectedRegion.trim();
              return parkRegionNormalized === selectedRegionNormalized ||
                     parkRegionNormalized.toLowerCase() === selectedRegionNormalized.toLowerCase();
            });
          }
          return true;
        });
        
        if (!currentFiltered.find(p => p.id === selectedPark)) {
          setSelectedPark(null);
          setParkData({});
        }
      }
      
      return newRegions;
    });
  };

  const handleSubRegionToggle = (subRegion) => {
    setSelectedSubRegions(prev => {
      if (prev.includes(subRegion)) {
        return prev.filter(r => r !== subRegion);
      } else {
        return [...prev, subRegion];
      }
    });
  };

  const loadParkData = async (parkId) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = {};
      const selectedParkInfo = allParks.find(p => p.id === parkId);
      const parkName = selectedParkInfo?.originalName || selectedParkInfo?.name;
      const parkIdValue = selectedParkInfo?.originalId || parkId;
      
      // Tüm şemalardaki tabloları tara ve park ile ilgili verileri çek
      const schemas = await databaseService.listSchemas();
      
      for (const schema of schemas) {
        try {
          const tables = await databaseService.listTables(schema);
          
          for (const table of tables) {
            try {
              const structure = await databaseService.getTableStructure(schema, table);
              
              const parkIdColumn = structure.find(col => {
                const colName = col.column_name.toLowerCase();
                return (colName.includes('park') && colName.includes('id')) || 
                       colName === 'park_id' || 
                       colName === 'parkid';
              });
              
              const parkNameColumn = structure.find(col => {
                const colName = col.column_name.toLowerCase();
                return (colName.includes('park') && (colName.includes('name') || colName.includes('ismi'))) ||
                       colName === 'park_name' ||
                       colName === 'park_ismi';
              });
              
              if (parkIdColumn) {
                const query = `SELECT * FROM ${schema}.${table} WHERE ${parkIdColumn.column_name} = ${parkIdValue} LIMIT 1000`;
                const result = await databaseService.executeQuery(query);
                
                if (result.rows && result.rows.length > 0) {
                  data[`${schema}.${table}`] = {
                    columns: result.columns,
                    rows: result.rows
                  };
                }
              } else if (parkNameColumn && parkName) {
                const query = `SELECT * FROM ${schema}.${table} WHERE ${parkNameColumn.column_name} = '${parkName.replace(/'/g, "''")}' LIMIT 1000`;
                const result = await databaseService.executeQuery(query);
                
                if (result.rows && result.rows.length > 0) {
                  data[`${schema}.${table}`] = {
                    columns: result.columns,
                    rows: result.rows
                  };
                }
              }
            } catch (err) {
              continue;
            }
          }
        } catch (err) {
          continue;
        }
      }
      
      setParkData(data);
      
      if (Object.keys(data).length === 0) {
        setError('Seçilen park için veri bulunamadı. Park ID veya adı ile eşleşen veri yok.');
      }
    } catch (err) {
      console.error('Park verisi yükleme hatası:', err);
      setError(err.message || 'Park verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (returnAsBase64 = false) => {
    if (!selectedPark || Object.keys(parkData).length === 0) {
      setError('Rapor oluşturmak için park seçin ve verileri yükleyin.');
      return null;
    }

    try {
      setGenerating(true);
      
      const park = allParks.find(p => p.id === selectedPark);
      const parkName = park?.name || `Park ${selectedPark}`;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const maxWidth = pageWidth - (margin * 2);
      
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Park Raporu', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      const parkNameText = doc.splitTextToSize(`Park: ${parkName}`, maxWidth);
      doc.text(parkNameText, pageWidth / 2, 30, { align: 'center' });
      if (park?.region) {
        doc.text(`Bölge: ${park.region}`, pageWidth / 2, 37, { align: 'center' });
      }
      doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth / 2, 44, { align: 'center' });
      
      let yPosition = 55;
      
      Object.keys(parkData).forEach((tableKey, tableIndex) => {
        const tableInfo = parkData[tableKey];
        const columns = tableInfo.columns;
        const rows = tableInfo.rows;
        
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const tableTitle = doc.splitTextToSize(tableKey, maxWidth);
        doc.text(tableTitle, margin, yPosition);
        yPosition += tableTitle.length * 6 + 4;
        
        if (rows.length === 0) {
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text('Bu tabloda veri bulunmuyor.', margin, yPosition);
          yPosition += 10;
          return;
        }
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        rows.forEach((row, rowIndex) => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setFont(undefined, 'bold');
          doc.text(`Satır ${rowIndex + 1}:`, margin, yPosition);
          yPosition += 6;
          
          columns.forEach((col) => {
            if (yPosition > pageHeight - 20) {
              doc.addPage();
              yPosition = 20;
            }
            
            const value = row[col] !== null && row[col] !== undefined 
              ? String(row[col]) 
              : 'NULL';
            
            doc.setFont(undefined, 'bold');
            const colNameText = `${col}: `;
            const colNameWidth = doc.getTextWidth(colNameText);
            doc.text(colNameText, margin + 10, yPosition);
            
            doc.setFont(undefined, 'normal');
            const valueText = doc.splitTextToSize(value, maxWidth - colNameWidth - 20);
            doc.text(valueText, margin + 10 + colNameWidth, yPosition);
            yPosition += valueText.length * 5 + 2;
          });
          
          yPosition += 8;
        });
        
        yPosition += 10;
      });
      
      const fileName = `Park_Raporu_${parkName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      if (returnAsBase64) {
        // PDF'i arraybuffer olarak al ve base64'e çevir (normal indirme ile aynı)
        const pdfArrayBuffer = doc.output('arraybuffer');
        // ArrayBuffer'ı base64'e çevir
        const uint8Array = new Uint8Array(pdfArrayBuffer);
        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const cleanBase64 = btoa(binaryString);
        setGenerating(false);
        return { pdfBase64: cleanBase64, fileName };
      } else {
        // Normal indirme
        doc.save(fileName);
        setGenerating(false);
        return null;
      }
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      setError('PDF oluşturulurken bir hata oluştu: ' + err.message);
      setGenerating(false);
      return null;
    }
  };

  const sendEmailWithPDF = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      setError('Geçerli bir e-posta adresi girin.');
      return;
    }

    try {
      setSendingEmail(true);
      setError(null);

      // PDF'i base64 olarak oluştur
      const pdfData = await generatePDF(true);
      if (!pdfData) {
        setError('PDF oluşturulamadı.');
        setSendingEmail(false);
        return;
      }

      // Backend API'ye gönder
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      if (!API_BASE_URL) {
        setError('Backend API endpoint tanımlı değil.');
        setSendingEmail(false);
        return;
      }

      const park = allParks.find(p => p.id === selectedPark);
      const parkName = park?.name || `Park ${selectedPark}`;

      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: emailTo.split(',').map(e => e.trim()),
          subject: emailSubject || `Park Raporu - ${parkName}`,
          body: emailBody || `${parkName} parkı için detaylı rapor ektedir.`,
          pdfBase64: pdfData.pdfBase64,
          pdfFileName: pdfData.fileName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'E-posta gönderilemedi' }));
        throw new Error(errorData.message || 'E-posta gönderilemedi');
      }

      const result = await response.json();
      
      if (result.success) {
        // Başarı bildirimi - toast notification
        const recipientCount = emailTo.split(',').length;
        const recipientText = recipientCount === 1 
          ? `E-posta başarıyla gönderildi!` 
          : `${recipientCount} alıcıya e-posta başarıyla gönderildi!`;
        
        setSuccessMessage(`${recipientText}\n\nPDF: ${pdfData.fileName}\nAlici: ${emailTo}`);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 5000);
        
        setShowEmailModal(false);
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
      } else {
        throw new Error(result.message || 'E-posta gönderilemedi');
      }
    } catch (err) {
      console.error('E-posta gönderme hatası:', err);
      setError('E-posta gönderilemedi: ' + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // E-posta konfigürasyonunu kontrol et
  useEffect(() => {
    const checkEmailConfig = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
        if (API_BASE_URL) {
          setEmailConfigEnabled(true);
        }
      } catch (err) {
        console.warn('E-posta konfigürasyonu kontrol edilemedi:', err);
        setEmailConfigEnabled(false);
      }
    };
    checkEmailConfig();
  }, []);

  const handleParkSelect = (parkId) => {
    setSelectedPark(parkId);
    setParkData({});
    if (parkId) {
      loadParkData(parkId);
    }
  };

  return (
    <div className={styles.reportsContainer}>
      <div className={styles.reportsHeader}>
        <h2>Park Raporları</h2>
        <p className={styles.reportsSubtitle}>
          Bölge seçin, parkları filtreleyin ve rapor oluşturun
        </p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className={styles.reportsContent}>
        {/* Bölge Seçimi */}
        <div className={styles.reportSelector}>
          <div className={styles.reportSelectorRow}>
            <label>Bölge Seçin (Çoklu Seçim) - {availableRegions.length} bölge bulundu:</label>
            {availableRegions.length === 0 ? (
              <p className={styles.noResults}>Bölge bilgisi bulunamadı. Veritabanını kontrol edin.</p>
            ) : (
              <div className={styles.multiSelectContainer}>
                {availableRegions.map(region => {
                  const parkCount = regionParkCounts[region] || 0;
                  return (
                    <label key={region} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedRegions.includes(region)}
                        onChange={() => handleRegionToggle(region)}
                        disabled={loading}
                      />
                      <span>
                        {region}
                        <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '12px', fontWeight: 'normal' }}>
                          ({parkCount} park)
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Alt Bölge Seçimi */}
          {subRegions.length > 0 && (
            <div className={styles.reportSelectorRow}>
              <label>Alt Bölge/Mahalle Seçin (Çoklu Seçim):</label>
              <div className={styles.multiSelectContainer}>
                {subRegions.map(subRegion => (
                  <label key={subRegion} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedSubRegions.includes(subRegion)}
                      onChange={() => handleSubRegionToggle(subRegion)}
                      disabled={loading}
                    />
                    <span>{subRegion}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Arama */}
          <div className={styles.reportSelectorRow}>
            <label htmlFor="parkSearch">Park Ara:</label>
            <input
              id="parkSearch"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Park adı, bölge veya ID ile ara..."
              className={styles.searchInput}
              disabled={loading}
            />
          </div>

          {/* Park Listesi */}
          <div className={styles.reportSelectorRow}>
            <label>Park Seçin ({filteredParks.length} park {selectedRegions.length > 0 ? `- ${selectedRegions.join(', ')} seçili` : ''}):</label>
            {filteredParks.length === 0 ? (
              <p className={styles.noResults}>
                {selectedRegions.length > 0 
                  ? `Seçilen bölgelerde park bulunamadı. Filtreleri değiştirin veya arama terimini güncelleyin.`
                  : 'Park bulunamadı. Bölge seçin veya arama yapın.'}
              </p>
            ) : (
              <div className={styles.parkListContainer}>
                <select
                  value={selectedPark || ''}
                  onChange={(e) => handleParkSelect(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={loading || filteredParks.length === 0}
                  className={styles.parkSelect}
                  size={Math.min(10, filteredParks.length)}
                >
                  <option value="">-- Park Seçin --</option>
                  {filteredParks.map(park => (
                    <option key={park.id} value={park.id} title={park.name}>
                      {park.name.length > 60 ? `${park.name.substring(0, 60)}...` : park.name}
                      {park.region && ` (${park.region})`}
                      {park.subRegion && ` - ${park.subRegion}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {selectedPark && (
            <>
              <div className={styles.selectedParkInfo}>
                <strong>Seçilen Park:</strong> {filteredParks.find(p => p.id === selectedPark)?.name || allParks.find(p => p.id === selectedPark)?.name || `Park ${selectedPark}`}
                {(filteredParks.find(p => p.id === selectedPark) || allParks.find(p => p.id === selectedPark))?.region && (
                  <span style={{ marginLeft: '12px', color: '#64748b' }}>
                    ({(filteredParks.find(p => p.id === selectedPark) || allParks.find(p => p.id === selectedPark))?.region})
                  </span>
                )}
              </div>
              <div className={styles.reportSelectorRow}>
                <div style={{ flex: 1 }}></div>
                <button
                  onClick={generatePDF}
                  disabled={generating || Object.keys(parkData).length === 0}
                  className={styles.generatePdfButton}
                >
                  {generating ? 'PDF Oluşturuluyor...' : 'PDF Indir'}
                </button>
                {emailConfigEnabled && (
                  <button
                    onClick={() => setShowEmailModal(true)}
                    disabled={generating || Object.keys(parkData).length === 0}
                    className={styles.generatePdfButton}
                    style={{ marginLeft: '10px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    E-posta ile Gönder
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>{selectedPark ? 'Park verileri yükleniyor...' : 'Park listesi yükleniyor...'}</p>
          </div>
        )}

        {selectedPark && Object.keys(parkData).length > 0 && (
          <div className={styles.parkDataPreview}>
            <h3>Park Verileri Önizleme</h3>
            <p className={styles.dataInfo}>
              {Object.keys(parkData).length} tabloda toplam{' '}
              {Object.values(parkData).reduce((sum, table) => sum + table.rows.length, 0)} kayıt bulundu.
            </p>
            
            {Object.keys(parkData).map((tableKey) => {
              const tableInfo = parkData[tableKey];
              return (
                <div key={tableKey} className={styles.parkDataTable}>
                  <h4>{tableKey}</h4>
                  <p>{tableInfo.rows.length} kayıt</p>
                </div>
              );
            })}
          </div>
        )}

        {selectedPark && !loading && Object.keys(parkData).length === 0 && (
          <div className={styles.emptyStateContainer}>
            <p>Bu park için veri bulunamadı.</p>
          </div>
        )}
      </div>

      {/* E-posta Gönderme Modal */}
      {showEmailModal && (
        <div className={styles.addUserModal}>
          <div className={styles.modalContent} style={{ maxWidth: '600px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: '700' }}>
                E-posta ile Gönder
              </h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailBody('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <div className={styles.formGroup}>
              <label>Alıcı E-posta Adresi(leri):</label>
              <input
                type="email"
                placeholder="ornek@email.com (virgülle ayırarak birden fazla ekleyebilirsiniz)"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                required
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
              <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Birden fazla e-posta için virgülle ayırın: email1@example.com, email2@example.com
              </small>
            </div>

            <div className={styles.formGroup}>
              <label>Konu:</label>
              <input
                type="text"
                placeholder="E-posta konusu"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
              {!emailSubject && (
                <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Boş bırakılırsa otomatik konu kullanılacak
                </small>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Mesaj:</label>
              <textarea
                placeholder="E-posta mesajı (opsiyonel)"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ 
              padding: '12px', 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
              borderRadius: '8px', 
              border: '1px solid rgba(59, 130, 246, 0.2)',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e293b' }}>
                <strong>Ek:</strong> PDF raporu otomatik olarak eklenecektir.
              </p>
            </div>

            <div className={styles.modalActions} style={{ 
              paddingTop: '20px',
              borderTop: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <button 
                type="button" 
                onClick={sendEmailWithPDF} 
                disabled={sendingEmail || !emailTo} 
                className={styles.submitButton}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                {sendingEmail ? 'Gönderiliyor...' : 'E-posta Gönder'}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailBody('');
                }}
                className={styles.cancelButton}
                disabled={sendingEmail}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast Notification */}
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
          minWidth: '350px',
          maxWidth: '500px',
          animation: 'slideInRight 0.3s ease-out',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>
              <span style={{ fontSize: '16px' }}></span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '8px',
                lineHeight: '1.4'
              }}>
                Başarılı!
              </div>
              <div style={{
                fontSize: '14px',
                lineHeight: '1.6',
                opacity: 0.95,
                whiteSpace: 'pre-line'
              }}>
                {successMessage}
              </div>
            </div>
            <button
              onClick={() => setShowSuccessToast(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
                flexShrink: 0
              }}
              onMouseOver={(e) => e.target.style.opacity = '1'}
              onMouseOut={(e) => e.target.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Reports;
